import { usePhone } from '@/hooks/usePhone';
import {
  ATTEMPT_REASON_LABEL,
  attemptMinutes,
  attemptPlatformLabel,
  attemptsLine,
  fixFor,
  milestoneDots,
  noFixWords,
  shortOfFirstCheck,
  stuckSourceWords,
  stuckWords,
  type AttemptView,
  type PersonRow,
} from '@/lib/people-data';
import { NOT_MEASURED, ago, day, num, when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * "Where they stopped, why" on the person page (ACTIVATION_FUNNEL_V1
 * section 1 and section 8 rule 5): the four leading milestones as dots
 * with their dates, the stuck reason in plain words with where the verdict
 * came from and the fix line the product itself sent, and the attempts the
 * client reported, newest first. Every value is off a row: a milestone not
 * reached is an empty dot, a reason not read says so, an attempt without
 * seconds says not measured. The reason is shown for a person short of a
 * first check, the way the funnel counts it; past that it no longer
 * describes where they are and the block says what the next milestone is.
 *
 * Under 640px the attempt table is one card per attempt, the card pattern
 * from the People list.
 */
export function StoppedWhy({ person: p, attempts, now }: { person: PersonRow; attempts: AttemptView[]; now?: number }) {
  const phone = usePhone();
  const dots = milestoneDots(p.milestones);
  const short = shortOfFirstCheck(p);
  const why = stuckWords(p);
  const fix = fixFor(p);
  const summary = attemptsLine(p.attempts);
  const detail = p.stuckDetail ?? {};
  const detailBits = Object.entries(detail)
    .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !['reason', 'why', 'at'].includes(k))
    .map(([k, v]) => `${k.replace(/_/g, ' ')} ${String(v)}`);

  return (
    <div className={s.section} style={{ padding: '14px 16px' }} data-panel="stopped-why">
      <div className={s.sectionTitle}>Where they stopped, why</div>

      <ol className={s.dots} aria-label="Milestones">
        {dots.map((d) => (
          <li key={d.milestone} className={`${s.dot} ${d.at ? s.dotOn : ''}`} data-milestone={d.milestone} data-reached={d.at ? '1' : '0'}>
            <span className={s.dotMark} aria-hidden="true" />
            <span className={s.dotLabel}>{d.label}</span>
            <span className={s.dotDate}>{d.at ? day(d.at) : 'not reached'}</span>
          </li>
        ))}
      </ol>

      <div className={s.whyBox} data-line="why">
        {short ? (
          <>
            <div className={s.whyLine}>
              Stuck before <strong>{p.stuckStepLabel ?? 'the first check'}</strong>: <strong className={why?.measured ? '' : s.missing}>{why?.text ?? 'not read yet'}</strong>
            </div>
            <div className={s.sub}>
              {stuckSourceWords(p)}
              {p.stuckBasis ? ` · ${when(p.stuckBasis.at)}` : ''}
              {detailBits.length ? ` · ${detailBits.join(' · ')}` : ''}
              {p.verificationLinkExpiredAt ? ` · verify link expired ${ago(p.verificationLinkExpiredAt, now)}` : ''}
            </div>
            {fix ? (
              <div className={s.fixLine} data-fix={fix.key}>
                <span className={s.fixLabel}>The fix the product sent</span>
                <span>“{fix.line}”</span>
              </div>
            ) : (
              <div className={`${s.sub} ${s.missing}`} data-fix="none">{noFixWords(p.stuckReason)}</div>
            )}
          </>
        ) : (
          <div className={s.whyLine}>
            Past the first check{p.milestones.first_check ? ` (${day(p.milestones.first_check)})` : ''}.{' '}
            {p.stuckStep ? <>Next milestone not reached: <strong>{p.stuckStepLabel}</strong>.</> : 'All seven milestones reached.'}
          </div>
        )}
      </div>

      <div className={s.groupTitle} style={{ marginTop: 12 }}>First-check attempts{summary ? ` · ${summary}` : ''}</div>
      {attempts.length === 0 && <div className={s.empty}>{p.attempts.count > 0 ? 'The attempt rows did not load this time.' : 'The client has reported no attempt. Nothing reached the camera step, or the client predates attempt reports.'}</div>}
      {attempts.length > 0 && phone && (
        <div className={s.people} data-panel="attempt-cards">
          {attempts.map((a) => (
            <article key={a.id} className={s.pCard} data-attempt={a.attemptN}>
              <div className={s.pCardTop}>
                <strong>Attempt {num(a.attemptN)}</strong>
                <span className={`${s.pill} ${a.outcome === 'failed' ? s.pill_failed : a.outcome === 'success' ? s.pill_paid : s.pill_new}`}>{a.outcome}</span>
              </div>
              <div className={s.pCardStuck}>{a.outcome === 'failed' ? (a.reasonLabel ?? (a.reason ? ATTEMPT_REASON_LABEL[a.reason] : 'reason not reported')) : a.outcome === 'success' ? 'baseline stored' : 'camera step opened'}{a.stage ? ` · ${a.stage.replace(/_/g, ' ')}` : ''}</div>
              <dl className={s.pCardNums}>
                <div className={s.pCardNum}><dt>Platform</dt><dd>{attemptPlatformLabel(a.platform)}</dd></div>
                <div className={s.pCardNum}><dt>Minutes</dt><dd>{a.seconds === null ? <span className={s.missing}>{NOT_MEASURED}</span> : attemptMinutes(a.seconds)}</dd></div>
                <div className={s.pCardNum}><dt>When</dt><dd>{when(a.at)}</dd></div>
                <div className={s.pCardNum}><dt>Ago</dt><dd>{ago(a.at, now)}</dd></div>
              </dl>
            </article>
          ))}
        </div>
      )}
      {attempts.length > 0 && !phone && (
        <div className={s.tableWrap}>
          <table className={s.table} data-panel="attempt-table">
            <thead><tr><th>Attempt</th><th>Outcome</th><th>Platform</th><th>Reason</th><th>Minutes</th><th>When</th></tr></thead>
            <tbody>
              {attempts.map((a) => (
                <tr key={a.id} data-attempt={a.attemptN}>
                  <td>{num(a.attemptN)}</td>
                  <td><span className={`${s.pill} ${a.outcome === 'failed' ? s.pill_failed : a.outcome === 'success' ? s.pill_paid : s.pill_new}`}>{a.outcome}</span></td>
                  <td>{attemptPlatformLabel(a.platform)}</td>
                  <td>{a.outcome === 'failed' ? (a.reasonLabel ?? (a.reason ? ATTEMPT_REASON_LABEL[a.reason] : 'reason not reported')) : a.outcome === 'success' ? 'baseline stored' : 'camera step opened'}{a.stage ? <div className={s.sub}>{a.stage.replace(/_/g, ' ')}</div> : null}</td>
                  <td>{a.seconds === null ? <span className={s.missing}>{NOT_MEASURED}</span> : attemptMinutes(a.seconds)}</td>
                  <td>{when(a.at)}<div className={s.sub}>{ago(a.at, now)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
